
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Property, AgentProfile, FilterState, AppTheme, User as UserType, AppSettings, Ad } from './types';
import { INITIAL_AGENTS, MOCK_PROPERTIES } from './constants';
import { ProfileHeader } from './components/ProfileHeader';
import { PropertyCard } from './components/PropertyCard';
import { PropertyPost } from './components/PropertyPost';
import { PropertyCompactRow } from './components/PropertyCompactRow';
import { SponsoredAdCompact } from './components/SponsoredAdCompact';
import { PropertyDetail } from './components/PropertyDetail';
import { PropertyForm } from './components/PropertyForm';
import { ProfileSettingsModal } from './components/ProfileSettingsModal';
import { SubscriptionModal } from './components/SubscriptionModal';
import { SponsoredAd } from './components/SponsoredAd';
import { SponsoredAdGrid } from './components/SponsoredAdGrid';
import { Filters } from './components/Filters';
import { AuthModal } from './components/AuthModal';
import { EmptyState } from './components/EmptyState';
import { Logo } from './components/Logo';
import { WelcomeModal } from './components/WelcomeModal';
import { Toast, ToastMessage, ToastType } from './components/Toast';
import { Moon, Sun, Grid, LayoutList, List, PlusSquare, ChevronRight, ChevronLeft, User, Settings, LogOut, CreditCard, Archive, Loader2, Info, FileText, Shield, PhoneCall, Mail, Smartphone, X, Megaphone, Instagram, Twitter, AlertTriangle, Users } from 'lucide-react';
import { supabase } from './supabase';

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<UserType | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  const [viewedAgentId, setViewedAgentId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get('agent') || null;
  });

  const [initialPropId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get('prop') || null;
  });

  const isOwnerMode = useMemo(() => {
    return !!user && !!viewedAgentId && user.agent_id === viewedAgentId;
  }, [user, viewedAgentId]);

  const [properties, setProperties] = useState<Property[]>([]);
  const [agents, setAgents] = useState<AgentProfile[]>(INITIAL_AGENTS);
  const [appSettings, setAppSettings] = useState<AppSettings | null>(null);
  const [ads, setAds] = useState<Ad[]>([]);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  
  const [filters, setFilters] = useState<FilterState>({
    keyword: '',
    ref: '',
    type: '',
    category: '',
    minPrice: '',
    maxPrice: '',
    status: '',
    bedrooms: 'Any',
    is_furnished: false,
    is_serviced: false,
    is_shared: false,
    timeframe: 'all'
  });

  const [viewMode, setViewMode] = useState<'grid' | 'compact' | 'feed' | 'archive'>('grid');
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [editingProperty, setEditingProperty] = useState<Property | null>(null);
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showContactGate, setShowContactGate] = useState(false);
  const [activeLegalModal, setActiveLegalModal] = useState<'about' | 'terms' | 'privacy' | 'contact' | null>(null);

  const [theme, setTheme] = useState<AppTheme>(() => {
    const saved = localStorage.getItem('findily-theme');
    return (saved as AppTheme) || 'dark';
  });
  
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  const updateMetaTag = (property: string, content: string, attr: 'name' | 'property' = 'property') => {
    let element = document.querySelector(`meta[${attr}="${property}"]`);
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attr, property);
      document.head.appendChild(element);
    }
    element.setAttribute('content', content);
  };

  useEffect(() => {
    let title = "Findily - Professional Property Catalog";
    let desc = "Discover elite real estate listings on Findily. Connect directly with professional agents.";
    let image = "https://xbmzppmojakpyevqnxvs.supabase.co/storage/v1/object/public/property-images/og-default.jpg";

    if (selectedProperty) {
      const priceStr = new Intl.NumberFormat('en-US', { style: 'currency', currency: selectedProperty.currency || 'USD', maximumFractionDigits: 0 }).format(selectedProperty.price);
      title = `${selectedProperty.title} (${priceStr}) | Findily`;
      desc = selectedProperty.description ? selectedProperty.description.substring(0, 160) : desc;
      if (selectedProperty.images && selectedProperty.images.length > 0) {
        image = selectedProperty.images[0];
      }
    } else if (viewedAgentId) {
      const agent = agents.find(a => a.id === viewedAgentId);
      if (agent) {
        title = `${agent.business_name || agent.name} | Findily`;
        desc = agent.bio ? agent.bio.substring(0, 160) : desc;
        image = agent.avatar || image;
      }
    }

    document.title = title;
    updateMetaTag('og:title', title);
    updateMetaTag('og:description', desc);
    updateMetaTag('og:image', image);
    updateMetaTag('twitter:title', title, 'name');
    updateMetaTag('twitter:description', desc, 'name');
    updateMetaTag('twitter:image', image, 'name');
  }, [selectedProperty, viewedAgentId, agents]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setIsSettingsOpen(false);
      }
    };
    if (isSettingsOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSettingsOpen]);

  const addToast = (message: string, type: ToastType = 'info') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const checkSubActive = (agent: AgentProfile | null | undefined) => {
    if (!agent) return false;
    if (agent.is_pioneer) return true;
    if (agent.plan === 'Free' || !agent.plan) return false;
    if (!agent.subscription_expiry) return false;
    return new Date(agent.subscription_expiry) > new Date();
  };

  const hasContactInfo = (agent: AgentProfile | null | undefined) => {
    return !!agent?.phone && !!agent?.whatsapp;
  };

  const activeAgent = useMemo(() => viewedAgentId ? agents.find(a => a.id === viewedAgentId) : null, [viewedAgentId, agents]);
  const currentUserAgent = useMemo(() => user ? agents.find(a => a.id === user.agent_id) : null, [user, agents]);

  const updateUrl = (agentId: string | null, propId: string | null, usePush = false) => {
    if (window.location.protocol === 'blob:') return;
    try {
      const url = new URL(window.location.origin);
      if (agentId) url.searchParams.set('agent', agentId);
      if (propId) url.searchParams.set('prop', propId);
      if (usePush) window.history.pushState({ agentId, propId }, '', url.toString());
      else window.history.replaceState({ agentId, propId }, '', url.toString());
    } catch (e) { console.warn("URL update failed:", e); }
  };

  const handlePopState = () => {
    const params = new URLSearchParams(window.location.search);
    const agentId = params.get('agent');
    const propId = params.get('prop');
    const status = params.get('status')?.toLowerCase();
    const koraStatus = params.get('kora_status')?.toLowerCase();
    if (status === 'success' || koraStatus === 'success') {
      addToast('Payment Successful!', 'success');
      setTimeout(() => updateUrl(agentId, propId, false), 1000);
    }
    setViewedAgentId(agentId);
    if (propId && properties.length > 0) {
      const prop = properties.find(p => p.id === propId) || MOCK_PROPERTIES.find(p => p.id === propId);
      if (prop) setSelectedProperty(prop);
    } else if (!propId) setSelectedProperty(null);
  };

  useEffect(() => {
    if (properties.length > 0 && initialPropId) {
      const found = properties.find(p => p.id === initialPropId) || MOCK_PROPERTIES.find(p => p.id === initialPropId);
      if (found) {
        setSelectedProperty(found);
        if (!viewedAgentId && found.agent_id) setViewedAgentId(found.agent_id);
      }
    }
  }, [properties, initialPropId]);

  useEffect(() => {
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [properties, user, isLoading]);

  const handleAuthSuccess = async (authUser: any, shouldNavigate = false) => {
    if (!supabase) return;
    setUser({ id: authUser.id, email: authUser.email || '', isLoggedIn: true, agent_id: authUser.id });
    
    // Fresh logins should take the user to their profile immediately
    if (shouldNavigate) {
      setViewedAgentId(authUser.id);
      updateUrl(authUser.id, null, true);
    }

    try {
      let { data: agent } = await supabase.from('agents').select('*').eq('id', authUser.id).maybeSingle();
      if (!agent) {
        const fullName = authUser.user_metadata?.full_name || authUser.email?.split('@')[0] || 'agent';
        const { data: newAgent } = await supabase.from('agents').insert([{
          id: authUser.id,
          name: fullName,
          full_name: fullName,
          phone: authUser.phone || '',
          whatsapp: authUser.phone || '',
          email: authUser.email,
          business_name: `${fullName.charAt(0).toUpperCase() + fullName.slice(1)} Estates`,
          bio: 'Premium real estate professional.',
          location: 'Lagos, Nigeria',
          avatar: authUser.user_metadata?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}`,
          plan: 'Basic',
          is_pioneer: true,
          verification_status: 'unverified'
        }]).select().single();
        agent = newAgent;
        setShowWelcomeModal(true);
      }
      if (agent) {
        setAgents(prev => prev.some(a => a.id === agent.id) ? prev.map(a => a.id === agent.id ? agent : a) : [...prev, agent]);
      }
    } catch (err) { console.error("Auth handler crash:", err); }
  };

  useEffect(() => {
    const init = async () => {
      if (!supabase) { setProperties(MOCK_PROPERTIES); setIsLoading(false); return; }
      try {
        const { data: { session } } = await supabase.auth.getSession();
        // On refresh/init, we don't want to force-redirect to profile
        if (session?.user) await handleAuthSuccess(session.user, false);
        const [propsRes, agentsRes, settingsRes, adsRes] = await Promise.all([
          supabase.from('properties').select('*'),
          supabase.from('agents').select('*'),
          supabase.from('app_settings').select('*').maybeSingle(),
          supabase.from('ads').select('*').eq('is_active', true)
        ]);
        if (propsRes.data?.length) setProperties(propsRes.data); else setProperties(MOCK_PROPERTIES);
        if (agentsRes.data) setAgents(prev => {
          const combined = [...prev];
          agentsRes.data!.forEach(a => { if (!combined.some(c => c.id === a.id)) combined.push(a); });
          return combined;
        });
        if (settingsRes.data) setAppSettings(settingsRes.data);
        if (adsRes.data) setAds(adsRes.data);
      } catch (err) { setProperties(MOCK_PROPERTIES); } finally { setIsLoading(false); }
    };
    init();
  }, []);

  const handleLogout = async () => {
    if (supabase) await supabase.auth.signOut();
    setUser(null);
    setIsSettingsOpen(false);
    setViewedAgentId(null);
    updateUrl(null, null, true);
    addToast("Logged out successfully.");
  };

  const handlePropertySelect = (prop: Property | null) => {
    setSelectedProperty(prop);
    if (prop) updateUrl(viewedAgentId, prop.id, true);
    else updateUrl(viewedAgentId, null, false);
  };

  const handleAgentNavigate = (agentId: string) => {
    setViewedAgentId(agentId);
    updateUrl(agentId, null, true);
    if (viewMode === 'archive' && user?.agent_id !== agentId) setViewMode('grid');
  };

  const handleAddPropertyTrigger = () => {
    if (!user) { setShowAuthModal(true); return; }
    if (!hasContactInfo(currentUserAgent)) { setShowContactGate(true); return; }
    setEditingProperty(null);
    setShowPropertyForm(true);
  };

  const filteredProperties = useMemo(() => {
    let list = [...properties].sort((a, b) => Number(b.created_at) - Number(a.created_at));
    if (viewedAgentId) {
      list = list.filter(p => p.agent_id === viewedAgentId);
      if (isOwnerMode) {
        if (viewMode === 'archive') list = list.filter(p => p.is_archived);
        else list = list.filter(p => !p.is_archived);
      } else list = list.filter(p => !p.is_archived && p.market_status === 'Available' && p.status === 'Available');
    } else {
      list = list.filter(p => {
        const agent = agents.find(a => a.id === p.agent_id);
        return checkSubActive(agent) && !p.is_archived && p.market_status === 'Available' && p.status === 'Available';
      });
    }
    if (filters.keyword.trim()) {
      const q = filters.keyword.toLowerCase().trim();
      list = list.filter(p => p.title.toLowerCase().includes(q) || p.location.toLowerCase().includes(q) || p.description.toLowerCase().includes(q));
    }
    if (filters.type) list = list.filter(p => p.type === filters.type);
    if (filters.category) list = list.filter(p => p.category === filters.category);
    if (filters.status) list = list.filter(p => p.market_status === filters.status || p.status === filters.status);
    if (filters.minPrice !== '') list = list.filter(p => p.price >= Number(filters.minPrice));
    if (filters.maxPrice !== '') list = list.filter(p => p.price <= Number(filters.maxPrice));
    return list;
  }, [properties, filters, viewedAgentId, isOwnerMode, agents, viewMode]);

  const itemsToRender = useMemo(() => {
    const items: { type: 'property' | 'ad'; data: Property | Ad }[] = [];
    filteredProperties.forEach((prop, index) => {
      items.push({ type: 'property', data: prop });
      if (!viewedAgentId && (index + 1) % 5 === 0 && ads.length > 0) {
        items.push({ type: 'ad', data: ads[Math.floor(Math.random() * ads.length)] });
      }
    });
    return items;
  }, [filteredProperties, ads, viewedAgentId]);

  useEffect(() => {
    if (theme === 'dark') document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [theme]);

  const handleSaveProperty = async (data: any) => {
    if (!supabase || !user) return;
    const isUpdate = !!editingProperty;
    const isActive = checkSubActive(currentUserAgent);
    const payload = { ...data, agent_id: user.agent_id };
    
    // Safety Logic: Unsubscribed agents listings are automatically archived/unpublished.
    if (!isActive) payload.is_archived = true;

    try {
      if (isUpdate) {
        const { data: updated, error } = await supabase.from('properties').update(payload).eq('id', editingProperty.id).select().single();
        if (error) throw error;
        setProperties(prev => prev.map(p => p.id === editingProperty.id ? updated : p));
        addToast(isActive ? "Listing updated!" : "Listing updated in archive.", "success");
      } else {
        // Use ISO string for created_at to match TIMESTAMPTZ
        const { data: inserted, error } = await supabase.from('properties').insert([{ 
          ...payload, 
          created_at: new Date().toISOString() 
        }]).select().single();
        
        if (error) throw error;
        setProperties(prev => [inserted, ...prev]);
        addToast(isActive ? "Listing published!" : "Listing saved to private archive.", "success");
      }
      setShowPropertyForm(false);
      setEditingProperty(null);
    } catch (e: any) { 
      console.error("Save error:", e);
      addToast(e.message || "Failed to save. Please try again.", "error"); 
      throw e; // Rethrow so the form knows it failed
    }
  };

  const handleEditClick = (prop: Property) => {
    setEditingProperty(prop);
    setShowPropertyForm(true);
    setSelectedProperty(null);
  };

  const handleArchiveProperty = async (id: string, archive: boolean) => {
    if (!supabase) return;
    if (!archive && !checkSubActive(currentUserAgent)) {
      addToast("Active subscription required to publish.", "error");
      setShowSubscriptionModal(true);
      return;
    }
    try {
      const { error } = await supabase.from('properties').update({ is_archived: archive }).eq('id', id);
      if (error) throw error;
      setProperties(prev => prev.map(p => p.id === id ? { ...p, is_archived: archive } : p));
      addToast(archive ? "Listing unpublished." : "Listing published!", archive ? "warning" : "success");
      setSelectedProperty(null);
    } catch (e) { addToast("Database error.", "error"); }
  };

  const handleDeleteProperty = async (id: string) => {
    if (!supabase) return;
    const { error } = await supabase.from('properties').delete().eq('id', id);
    if (!error) {
      setProperties(prev => prev.filter(p => p.id !== id));
      setSelectedProperty(null);
      addToast("Listing permanently deleted.", "error");
    }
  };

  const handleProfileUpdate = (updates: Partial<AgentProfile>) => {
    setAgents(prev => prev.map(a => a.id === (viewedAgentId || user?.agent_id) ? { ...a, ...updates } : a));
    addToast("Profile updated.", "success");
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${theme === 'dark' ? 'bg-black' : 'bg-gray-50'}`}>
      <nav className="sticky top-0 z-50 bg-white/70 dark:bg-black/80 backdrop-blur-xl px-5 py-4 flex items-center justify-between border-b border-gray-100 dark:border-zinc-900">
        <div className="flex items-center gap-6">
          <button onClick={() => { setViewedAgentId(null); updateUrl(null, null, true); setViewMode('grid'); }} className="flex items-center gap-2 hover:opacity-70 transition-all active:scale-95">
            {viewedAgentId && <ChevronLeft className="w-6 h-6 text-black dark:text-white" />}
            <Logo withText size={28} />
          </button>
        </div>
        <div className="flex items-center gap-4 relative" ref={settingsRef}>
          <button onClick={() => setIsSettingsOpen(!isSettingsOpen)} className={`p-2 rounded-lg transition-all active:scale-95 ${isSettingsOpen ? 'bg-gray-100 dark:bg-zinc-800' : 'hover:bg-gray-50 dark:hover:bg-zinc-900'} text-black dark:text-white`}><Settings className="w-6 h-6 stroke-[1.5]" /></button>
          {isSettingsOpen && (
            <div className="absolute top-full right-0 mt-2 w-64 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl border border-gray-100 dark:border-zinc-800 rounded-2xl shadow-2xl py-2 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
              <div className="px-4 py-3 border-b border-gray-100 dark:border-zinc-800">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Appearance</p>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <button onClick={() => setTheme('light')} className={`flex items-center justify-center gap-2 py-2 rounded-xl text-[10px] font-black uppercase border-2 transition-all ${theme === 'light' ? 'bg-black text-white border-black' : 'border-gray-100 dark:border-zinc-800 text-gray-400'}`}><Sun className="w-3.5 h-3.5" /> Light</button>
                  <button onClick={() => setTheme('dark')} className={`flex items-center justify-center gap-2 py-2 rounded-xl text-[10px] font-black uppercase border-2 transition-all ${theme === 'dark' ? 'bg-white text-black border-white' : 'border-gray-100 dark:border-zinc-800 text-gray-400'}`}><Moon className="w-3.5 h-3.5" /> Dark</button>
                </div>
              </div>
              <div className="py-1">
                {user && (
                  <>
                    <button onClick={() => { setIsSettingsOpen(false); handleAddPropertyTrigger(); }} className="w-full px-4 py-3 flex items-center gap-3 text-sm font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-all"><PlusSquare className="w-4 h-4" /> Add Listing</button>
                    <button onClick={() => { setShowProfileSettings(true); setIsSettingsOpen(false); }} className="w-full px-4 py-3 flex items-center gap-3 text-sm font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-all"><Settings className="w-4 h-4" /> Profile Settings</button>
                    <button onClick={() => { setShowSubscriptionModal(true); setIsSettingsOpen(false); }} className="w-full px-4 py-3 flex items-center gap-3 text-sm font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-all"><CreditCard className="w-4 h-4" /> My Subscription</button>
                  </>
                )}
                <div className="mt-2 pt-2 border-t border-gray-100 dark:border-zinc-800">
                  <p className="px-4 py-1 text-[9px] font-black uppercase tracking-widest text-gray-400">Information</p>
                  <button onClick={() => { setActiveLegalModal('about'); setIsSettingsOpen(false); }} className="w-full px-4 py-2.5 flex items-center gap-3 text-[13px] font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-all"><Info className="w-3.5 h-3.5" /> About Findily</button>
                  <button onClick={() => { setActiveLegalModal('terms'); setIsSettingsOpen(false); }} className="w-full px-4 py-2.5 flex items-center gap-3 text-[13px] font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-all"><FileText className="w-3.5 h-3.5" /> Terms of Service</button>
                  <button onClick={() => { setActiveLegalModal('privacy'); setIsSettingsOpen(false); }} className="w-full px-4 py-2.5 flex items-center gap-3 text-[13px] font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-all"><Shield className="w-3.5 h-3.5" /> Privacy Policy</button>
                  <button onClick={() => { setActiveLegalModal('contact'); setIsSettingsOpen(false); }} className="w-full px-4 py-2.5 flex items-center gap-3 text-[13px] font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-all"><PhoneCall className="w-3.5 h-3.5" /> Contact Us</button>
                </div>
                {user && <button onClick={handleLogout} className="w-full px-4 py-3 flex items-center gap-3 text-sm font-bold text-red-500 border-t border-gray-100 dark:border-zinc-800 mt-2"><LogOut className="w-4 h-4" /> Logout</button>}
              </div>
            </div>
          )}
          <User className={`w-6 h-6 cursor-pointer stroke-[1.5] transition-all active:scale-95 ${isOwnerMode ? 'text-blue-500' : 'text-black dark:text-white'}`} onClick={() => { if (!user) setShowAuthModal(true); else { setViewedAgentId(user.agent_id); updateUrl(user.agent_id, null, true); } }} />
        </div>
      </nav>

      <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[100] flex flex-col gap-2 w-full max-sm px-4 pointer-events-none">
        {toasts.map(t => <div key={t.id} className="pointer-events-auto"><Toast toast={t} onClose={removeToast} /></div>)}
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center min-h-[80vh]"><Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" /></div>
      ) : (
        <div className="max-w-4xl mx-auto pb-20">
          {!viewedAgentId && (
            <div className="px-5 pt-8 pb-4 text-center">
              <h1 className="text-3xl font-black tracking-tighter text-gray-900 dark:text-white">Discover on Findily</h1>
              <p className="text-xs font-bold text-gray-400 dark:text-zinc-500 uppercase tracking-widest mt-1">Premium real estate catalog</p>
            </div>
          )}
          {viewedAgentId && activeAgent && (
            <ProfileHeader 
              agent={activeAgent} 
              isOwner={isOwnerMode} 
              listingCount={filteredProperties.length} 
              onAddProperty={handleAddPropertyTrigger} 
              onEditProfile={() => setShowProfileSettings(true)} 
              onLogout={handleLogout} 
              onUpgrade={() => setShowSubscriptionModal(true)} 
              onToast={addToast}
            />
          )}
          <div className="px-4 mt-4 mb-6"><Filters filters={filters} setFilters={setFilters} isGridView={viewMode === 'grid'} setIsGridView={(v) => setViewMode(v ? 'grid' : 'feed')} /></div>
          <div className="flex border-t border-gray-100 dark:border-zinc-900">
            <button onClick={() => setViewMode('grid')} className={`flex-1 flex items-center justify-center py-3 border-t-2 ${viewMode === 'grid' ? 'border-black dark:border-white text-black dark:text-white' : 'border-transparent text-gray-400'}`}><Grid className="w-5 h-5" /></button>
            <button onClick={() => setViewMode('compact')} className={`flex-1 flex items-center justify-center py-3 border-t-2 ${viewMode === 'compact' ? 'border-black dark:border-white text-black dark:text-white' : 'border-transparent text-gray-400'}`}><List className="w-5 h-5" /></button>
            <button onClick={() => setViewMode('feed')} className={`flex-1 flex items-center justify-center py-3 border-t-2 ${viewMode === 'feed' ? 'border-black dark:border-white text-black dark:text-white' : 'border-transparent text-gray-400'}`}><LayoutList className="w-5 h-5" /></button>
            {isOwnerMode && <button onClick={() => setViewMode('archive')} className={`flex-1 flex items-center justify-center py-3 border-t-2 ${viewMode === 'archive' ? 'border-black dark:border-white text-black dark:text-white' : 'border-transparent text-gray-400'}`}><Archive className="w-5 h-5" /></button>}
          </div>
          {filteredProperties.length > 0 ? (
            <div className={viewMode === 'grid' || viewMode === 'archive' ? 'grid grid-cols-3 gap-[1px]' : 'flex flex-col'}>
              {viewMode === 'feed' ? (
                itemsToRender.map((item, idx) => item.type === 'property' ? <PropertyPost key={item.data.id} property={item.data as Property} agent={agents.find(a => a.id === (item.data as Property).agent_id) || agents[0]} onDetail={handlePropertySelect} onAgentClick={handleAgentNavigate} /> : <SponsoredAd key={`ad-${idx}`} ad={item.data as Ad} />)
              ) : viewMode === 'compact' ? (
                itemsToRender.map((item) => item.type === 'property' ? <PropertyCompactRow key={item.data.id} property={item.data as Property} agent={agents.find(a => a.id === (item.data as Property).agent_id) || agents[0]} onDetail={handlePropertySelect} /> : <SponsoredAdCompact key={item.data.id} ad={item.data as Ad} />)
              ) : (
                itemsToRender.map((item, idx) => item.type === 'property' ? <PropertyCard key={item.data.id} property={item.data as Property} agentAvatar={agents.find(a => a.id === (item.data as Property).agent_id)?.avatar} onPropertyClick={handlePropertySelect} onAgentClick={handleAgentNavigate} /> : <SponsoredAdGrid key={`ad-grid-${idx}`} ad={item.data as Ad} />)
              )}
            </div>
          ) : (
            <EmptyState viewMode={viewMode} onReset={() => { if (viewMode === 'archive') setViewMode('grid'); setFilters({ ...filters, keyword: '', category: '' }); }} />
          )}
        </div>
      )}

      {showContactGate && (
        <div className="fixed inset-0 z-[350] flex items-center justify-center p-4 bg-black/90 backdrop-blur-2xl animate-in fade-in duration-300">
           <div className="bg-white dark:bg-zinc-950 w-full max-sm rounded-[32px] overflow-hidden shadow-2xl relative p-8 text-center space-y-6 animate-in zoom-in-95 duration-500">
              <div className="w-20 h-20 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto">
                 <Smartphone className="w-10 h-10 text-blue-500" />
              </div>
              <div className="space-y-2">
                 <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tighter uppercase">Contact Info Required</h2>
                 <p className="text-xs font-medium text-gray-500 dark:text-zinc-500 leading-relaxed">
                   To list a property, please provide your **Phone** and **WhatsApp** numbers so buyers can reach you easily.
                 </p>
              </div>
              <div className="flex flex-col gap-3">
                 <button onClick={() => { setShowContactGate(false); setShowProfileSettings(true); }} className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest active:scale-95 transition-all">Update Profile Now</button>
                 <button onClick={() => setShowContactGate(false)} className="w-full py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">Maybe Later</button>
              </div>
           </div>
        </div>
      )}

      {activeLegalModal && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl animate-in fade-in duration-300" onClick={() => setActiveLegalModal(null)}>
          <div className="bg-white dark:bg-zinc-950 w-full max-w-lg rounded-[32px] overflow-hidden shadow-2xl relative flex flex-col max-h-[85vh]" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 dark:border-zinc-900 flex justify-between items-center bg-white/50 dark:bg-zinc-950/50 backdrop-blur-md sticky top-0 z-10">
               <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight uppercase">
                 {activeLegalModal === 'about' && 'About Findily'}
                 {activeLegalModal === 'terms' && 'Terms of Service'}
                 {activeLegalModal === 'privacy' && 'Privacy Policy'}
                 {activeLegalModal === 'contact' && 'Contact Us'}
               </h2>
               <button onClick={() => setActiveLegalModal(null)} className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-900 rounded-full transition-colors"><X className="w-6 h-6 text-gray-400" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-8 space-y-6 scrollbar-hide">
              {activeLegalModal === 'contact' ? (
                <div className="space-y-8">
                  <div className="grid grid-cols-1 gap-4">
                    <a href={`mailto:${appSettings?.support_email}`} className="flex items-center gap-4 p-5 bg-gray-50 dark:bg-zinc-900 rounded-2xl hover:bg-gray-100 dark:hover:bg-zinc-800 transition-all">
                      <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500"><Mail className="w-5 h-5" /></div>
                      <div><p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Email Support</p><p className="text-sm font-black dark:text-white">{appSettings?.support_email}</p></div>
                    </a>
                    
                    {appSettings?.community_url && (
                      <a href={appSettings.community_url} target="_blank" className="flex items-center gap-4 p-5 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 text-white rounded-2xl hover:opacity-90 transition-all shadow-xl shadow-purple-500/20">
                        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"><Users className="w-5 h-5" /></div>
                        <div><p className="text-[10px] font-black opacity-70 uppercase tracking-widest">Growth Tribe</p><p className="text-sm font-black">Join Our Community</p></div>
                      </a>
                    )}

                    {appSettings?.support_whatsapp && (
                      <a href={`https://wa.me/${appSettings.support_whatsapp}`} target="_blank" className="flex items-center gap-4 p-5 bg-gray-50 dark:bg-zinc-900 rounded-2xl hover:bg-gray-100 dark:hover:bg-zinc-800 transition-all">
                        <div className="w-10 h-10 rounded-full bg-green-600/10 flex items-center justify-center text-green-600"><Smartphone className="w-5 h-5" /></div>
                        <div><p className="text-[10px] font-black uppercase tracking-widest">Direct Assistance</p><p className="text-sm font-black dark:text-white">Chat with Support</p></div>
                      </a>
                    )}
                    
                    {appSettings?.place_ad_url && <a href={appSettings.place_ad_url} target="_blank" className="flex items-center gap-4 p-5 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 transition-all shadow-xl shadow-blue-500/20"><div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"><Megaphone className="w-5 h-5" /></div><div><p className="text-[10px] font-black opacity-70 uppercase tracking-widest">Business Partnership</p><p className="text-sm font-black">Advertise with Us</p></div></a>}
                  </div>
                  <div className="pt-6 border-t border-gray-100 dark:border-zinc-900">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest text-center mb-4">Follow Our Official Handles</p>
                    <div className="flex justify-center gap-4">
                      {appSettings?.instagram_url && <a href={appSettings.instagram_url} target="_blank" className="p-4 bg-gray-50 dark:bg-zinc-900 rounded-full hover:bg-gray-100 dark:hover:bg-zinc-800 transition-all text-pink-600"><Instagram className="w-6 h-6" /></a>}
                      {appSettings?.twitter_url && <a href={appSettings.twitter_url} target="_blank" className="p-4 bg-gray-50 dark:bg-zinc-900 rounded-full hover:bg-gray-100 dark:hover:bg-zinc-800 transition-all text-blue-400"><Twitter className="w-6 h-6" /></a>}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-gray-600 dark:text-zinc-400 text-sm font-medium leading-relaxed whitespace-pre-wrap">{activeLegalModal === 'about' && (appSettings?.about_us_text || "Findily is a modern property catalog platform.")}{activeLegalModal === 'terms' && (appSettings?.terms_text || "Terms of Service details.")}{activeLegalModal === 'privacy' && (appSettings?.privacy_text || "Privacy Policy details.")}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedProperty && (
        <PropertyDetail 
          property={selectedProperty} 
          agent={agents.find(a => a.id === selectedProperty.agent_id) || agents[0]} 
          onClose={() => handlePropertySelect(null)} 
          onAgentClick={handleAgentNavigate} 
          isOwner={user?.agent_id === selectedProperty.agent_id} 
          onDeleteClick={handleDeleteProperty}
          onArchiveClick={handleArchiveProperty}
          onEditClick={handleEditClick}
          onToast={addToast}
        />
      )}
      {showPropertyForm && currentUserAgent && <PropertyForm agent={currentUserAgent} onSave={handleSaveProperty} onClose={() => { setShowPropertyForm(false); setEditingProperty(null); }} initialData={editingProperty || undefined} isSubscribed={checkSubActive(currentUserAgent)} />}
      {showProfileSettings && activeAgent && <ProfileSettingsModal agent={activeAgent} onClose={() => setShowProfileSettings(false)} onUpdate={handleProfileUpdate} />}
      {showSubscriptionModal && user && currentUserAgent && <SubscriptionModal agent={currentUserAgent} onClose={() => setShowSubscriptionModal(false)} />}
      {showWelcomeModal && currentUserAgent && <WelcomeModal agent={currentUserAgent} onClose={() => setShowWelcomeModal(false)} onUpdate={handleProfileUpdate} />}
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} onSuccess={(authUser) => handleAuthSuccess(authUser, true)} />}
    </div>
  );
};

export default App;
